/**
 * F18.G.3 — Specialization Lab handoff contract.
 *
 * Cross-component handshake for surfaces that want to open the
 * Specialization Lab pre-seeded with a specific skill (and optionally a
 * specific probe prompt). Used by:
 *   - TrainLoraButton — passes the just-trained skillName.
 *   - BundleSpecializeButton — passes the first skill in the bundle.
 *   - NIPE `openSpecializationLab` tool — passes the LLM-suggested
 *     skill + optional probe text.
 *
 * Mechanism: localStorage write-then-open. The Lab reads + clears the
 * key on mount. A timestamp gate (default 8s) prevents stale leftover
 * state from triggering a spurious seed.
 *
 * Why localStorage (not a Convex table, not a DOM event, not the
 * read-only `window.__repancySpecializationLab` bridge):
 *   - Synchronous, so the Lab can apply the seed during its first
 *     render — no flash-of-default-state.
 *   - No Convex schema change.
 *   - Survives the brief gap between `openWindow('/specialization-lab')`
 *     and the Lab actually mounting (window-manager animation, lazy
 *     code-splits).
 *   - Auto-cleared on read, so re-opening the Lab later doesn't
 *     re-apply old seeds.
 *
 * Per L.15 D4 — the `window.__repancySpecializationLab` bridge stays
 * read-only (getters only). This handshake lives one layer below the
 * bridge.
 */

const STORAGE_KEY = '__repancy_lab_pending';
const DEFAULT_MAX_AGE_MS = 8_000;

export interface LabHandoff {
  /** Skill name (matches `skillMarketplace.name`). */
  skillName?: string | null;
  /** Optional probe text — overrides the skill's curated probe. */
  probeText?: string;
  /**
   * Optional source label for debugging. Not consumed by the Lab; just
   * showed up in DevTools so you can tell which surface seeded the
   * handoff.
   */
  source?: string;
  /** Monotonic timestamp (Date.now()). Required to gate staleness. */
  timestamp: number;
}

/**
 * Write a handoff record. Caller should immediately follow with
 * `openWindow('/specialization-lab')`.
 *
 * Safe to call from server-rendered code paths (no-ops when
 * `window`/`localStorage` is unavailable).
 */
export function setLabHandoff(
  payload: Omit<LabHandoff, 'timestamp'>,
): void {
  if (typeof window === 'undefined') return;
  try {
    const record: LabHandoff = {
      ...payload,
      timestamp: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* private-mode / quota — non-fatal */
  }
}

/**
 * Read + clear the handoff record. Returns null when:
 *   - localStorage is unavailable
 *   - no record present
 *   - record is malformed JSON
 *   - record is older than `maxAgeMs`
 *
 * The record is ALWAYS removed on read, even if it's stale, so the
 * next mount of the Lab starts clean.
 */
export function consumeLabHandoff(
  maxAgeMs: number = DEFAULT_MAX_AGE_MS,
): LabHandoff | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    parsed === null ||
    typeof parsed !== 'object' ||
    typeof (parsed as LabHandoff).timestamp !== 'number'
  ) {
    return null;
  }
  const rec = parsed as LabHandoff;
  if (Date.now() - rec.timestamp > maxAgeMs) return null;
  return rec;
}

/** Test seam — wipes any pending record. */
export function __resetLabHandoff(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}
